'use client';

import { Content, Header, Item, Root, Trigger } from '@radix-ui/react-accordion';
import type { ReactNode } from 'react';
import { Text } from '../Text';
import { Typography } from '../Typography';
import { TextSectionBlock, TextSectionBlockProps } from '../TextSectionBlock';
import { Container } from '../Container';
import { IconResolver } from '../IconResolver';

export type AccordionProps = {
	className?: string;
	textSectionBlock?: Omit<TextSectionBlockProps, 'size'>;
	items?: Array<{
		title?: string;
		content?: ReactNode;
	}>;
};

export const Accordion = function (props: AccordionProps) {
	return (
		<section className={props.className} data-component='Accordion'>
			<Container className='flex flex-col gap-spacing-lg lg:flex-row lg:gap-20'>
				<div className='lg:flex-[0_0_30%]'>{props.textSectionBlock && <TextSectionBlock size='md' {...props.textSectionBlock} />}</div>

				<Root className='w-full grid items-start gap-spacing-5xs max-w-max-md mx-auto lg:mr-0' type='multiple'>
					{props.items?.map((item, index) => {
						return (
							<Item
								key={`accordion-item-${item.title}-${index}`}
								value={`accordion-item-${index}`}
								className={`rounded-md transition-[background] duration-fast ease-smooth hover:bg-background-brand-opacity-5 data-[state="open"]:bg-background-brand-opacity-5`}
							>
								<Header className={`animate-accordion-open`}>
									<Trigger
										className={`py-responsive-md group items-center flex justify-between gap-2 text-left w-full transition-all duration-fast hover:px-responsive-md data-[state=open]:px-responsive-md`}
									>
										<Text className='text-text-primary' styleType='heading-3xs'>
											{item.title}
										</Text>
										<div
											className={`grid transition-transform duration-normal ease-smooth text-foreground-brand-primary group-data-[state=open]:-scale-y-100`}
										>
											<IconResolver
												icon={'plus'}
												className={`col-start-1 col-end-1 row-start-1 row-end-1 transition-opacity duration-fast ease-smooth group-data-[state=open]:opacity-0`}
											/>
											<IconResolver
												icon={'minus'}
												className={`col-start-1 col-end-1 row-start-1 row-end-1 opacity-0 transition-opacity duration-fast ease-smooth group-data-[state=open]:opacity-100`}
											/>
										</div>
									</Trigger>
								</Header>
								<Content
									className={`overflow-hidden data-[state=open]:animate-accordion-open data-[state=closed]:animate-accordion-closed`}
								>
									<Typography className='text-text-secondary px-responsive-md pb-responsive-lg' styleType='body-md'>
										{item.content}
									</Typography>
								</Content>
							</Item>
						);
					})}
				</Root>
			</Container>
		</section>
	);
};
